import { Eye, Lock, Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";
import { ConfirmModal, DataTable, PageHeader } from "../../components/ui/patrones";
import { Aviso, Boton, Etiqueta } from "../../components/ui/primitivos";
import { borrarRol, fetchRoles, type Rol } from "../../lib/consola-api";
import { type Orden, ordenarLista, siguienteOrden } from "../../lib/orden";
import { useSession } from "../../lib/SessionContext";
import { useDatos } from "../../lib/useDatos";

/**
 * Listado de roles — porteada de `RolesListPage` de `imprenta`.
 *
 * El alta y la edición viven en `/roles/nuevo` y `/roles/:nombre` (ADR 0068):
 * hasta el 2026-08-22 eran un modal, y el editor de permisos no entraba.
 *
 * Diferencias con `imprenta`, todas por ADR:
 *  - **Sin nivel jerarquico**: la autoridad se decide por contencion (ADR 0015).
 *  - **Los roles base no se editan** (ADR 0014): alla sus permisos eran filas y
 *    se tocaban desde la consola; aca son codigo. Se muestran en solo lectura,
 *    que es mas informativo que ocultarlos.
 *  - **el catálogo `PERMISSIONS`**, con los tres de alcance `_all` (ADR 0015).
 */
export function RolesPagina() {
  const { can } = useSession();
  const navegar = useNavigate();
  const roles = useDatos(fetchRoles);

  const [aBorrar, setABorrar] = useState<Rol | null>(null);
  const [orden, setOrden] = useState<Orden | null>(null);
  const [error, setError] = useState<string | null>(null);

  const puedeCrear = can("roles", "create");
  const puedeEditar = can("roles", "permisos.update");
  const puedeBorrar = can("roles", "delete");

  async function confirmarBorrado() {
    if (!aBorrar) return;
    try {
      await borrarRol(aBorrar.nombre);
      setABorrar(null);
      setError(null);
      await roles.recargar();
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo borrar el rol");
    }
  }

  return (
    <div className="space-y-4">
      <PageHeader
        titulo="Roles"
        subtitulo="Los roles base son inmutables; los personalizados se crean acá"
        acciones={
          puedeCrear ? (
            <Boton onClick={() => navegar("/roles/nuevo")}>
              <Plus className="mr-2 inline h-4 w-4" aria-hidden="true" />
              Nuevo Rol
            </Boton>
          ) : undefined
        }
      />

      {error ? <Aviso>{error}</Aviso> : null}
      {roles.error ? <Aviso>{roles.error}</Aviso> : null}

      {roles.cargando && !roles.datos ? (
        // Centrado y con alto propio, como allá: un renglón contra el borde se
        // lee como «no hay roles», no como «está cargando».
        <div className="flex h-64 items-center justify-center text-neutral-500">
          Cargando roles...
        </div>
      ) : (
        <DataTable
          datos={ordenarLista(roles.datos ?? [], orden, (r, campo) => {
            switch (campo) {
              case "nombre":
                return r.nombre;
              case "descripcion":
                return r.descripcion;
              case "tipo":
                return r.tipo;
              case "miembros":
                return r.miembros;
              default:
                return null;
            }
          })}
          orden={orden}
          alOrdenar={(campo) => setOrden((o) => siguienteOrden(o, campo))}
          clave={(r) => r.nombre}
          cargando={roles.cargando}
          mensajeVacio="No hay roles"
          columnas={[
            {
              clave: "nombre",
              ordenarPor: "nombre",
              encabezado: "Nombre",
              className: "font-medium",
              // El candado de allá: dice que el rol no se toca ANTES de
              // abrirlo y encontrarse el formulario en solo lectura.
              render: (r) => (
                <span className="flex items-center gap-2">
                  {r.nombre}
                  {r.editable ? null : (
                    <Lock aria-label="Rol de sistema" className="h-3.5 w-3.5 text-neutral-400" />
                  )}
                </span>
              ),
            },
            {
              clave: "descripcion",
              ordenarPor: "descripcion",
              encabezado: "Descripción",
              className: "text-neutral-600",
              render: (r) => r.descripcion ?? "—",
            },
            {
              clave: "tipo",
              ordenarPor: "tipo",
              encabezado: "Tipo",
              render: (r) => (
                <Etiqueta tono={r.tipo === "base" ? "neutral" : "ok"}>
                  {r.tipo === "base" ? "Sistema" : "Personalizado"}
                </Etiqueta>
              ),
            },
            {
              clave: "miembros",
              ordenarPor: "miembros",
              encabezado: "Usuarios",
              className: "text-neutral-600",
              render: (r) => r.miembros,
            },
            {
              clave: "acciones",
              encabezado: "",
              className: "text-right",
              render: (r) => (
                <div className="flex justify-end gap-1">
                  {/* Abrir un rol es NAVEGAR, no montar un modal (ADR 0064). */}
                  <Boton
                    variante="fantasma"
                    tamano="icono"
                    aria-label={`${r.editable && puedeEditar ? "Editar" : "Ver"} el rol ${r.nombre}`}
                    title={r.editable && puedeEditar ? "Editar" : "Ver"}
                    onClick={() => navegar(`/roles/${encodeURIComponent(r.nombre)}`)}
                  >
                    {r.editable && puedeEditar ? (
                      <Pencil className="h-4 w-4" aria-hidden="true" />
                    ) : (
                      <Eye className="h-4 w-4" aria-hidden="true" />
                    )}
                  </Boton>
                  {r.editable && puedeBorrar ? (
                    <Boton
                      variante="fantasma"
                      tamano="icono"
                      className="text-red-600 hover:bg-red-50 hover:text-red-700"
                      aria-label={`Borrar el rol ${r.nombre}`}
                      title="Borrar"
                      onClick={() => setABorrar(r)}
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    </Boton>
                  ) : null}
                </div>
              ),
            },
          ]}
        />
      )}

      <ConfirmModal
        abierto={aBorrar !== null}
        onCerrar={() => setABorrar(null)}
        onConfirmar={() => void confirmarBorrado()}
        titulo="Eliminar rol"
        descripcion={
          aBorrar
            ? `¿Eliminar el rol "${aBorrar.nombre}"? Esta acción no se puede deshacer. Si está asignado a alguien, el servidor lo va a rechazar.`
            : ""
        }
        textoConfirmar="Eliminar"
        variante="destructiva"
      />
    </div>
  );
}
